// eslint-disable @typescript-eslint/ban-ts-comment
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import isNil from 'lodash/isNil';
import { useLocation } from 'react-router-dom';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { useLocalData } from 'hooks/helpers/useLocalData';
import { useCreatePortfolioSelectors, useCreatePortfolioState } from 'datasource/CreatePortfolio';
import { AdaptedConfigType, StepperConfigType, StepperPropType } from './types/StepperPropType';
import StepperItem from './StepperItem';
import { Icon, Loader } from '../../ui-library';
import { ICON_TYPE_DROPDOWN } from '../../ui-library/components/Icon';
import { flattenArray } from '../../utils/formatting';

import './Stepper.css';
import useGetMandatoryStep from './useGetMandatoryStep';

const getNumbersBetween = (start: number, end: number, step: number = 0.1, length = Math.ceil((end - start) / step)): number[] => {
    const result = Array.from({ length }, (_, i) => parseFloat((start + i * step).toFixed(1))).filter(item => item !== end);

    return result;
};

const Stepper = ({
    config,
    title,
    className,
    currentStepNumber = 2,
    productType = '',
}: StepperPropType) => {
    const { t } = useTranslation();
    const [completionPercentage, setCompletionPercentage] = useState(0);
    const [selectedStep, setSelectedStep] = useState<AdaptedConfigType>({} as AdaptedConfigType);
    const [adaptedConfig, setAdaptedConfig] = useState<AdaptedConfigType[]>();
    const [showSection, setShowSection] = useState<{ [key: string]: boolean }>({});
    const [completedSteps, setCompletedSteps, clear] = useLocalData<number[]>({
        storageType: 'sessionStorage',
        key: 'advisory--completedSteps',
        initialValue: [],
    });

    const [clickedStep, setClickedStep] = useState<string | null>(null);

    const { setStep, unlockNavigationAction } = usePortfolioProcessNavigation();
    const { continueDisabledFromShoppingCart, isProductSetup } = useCreatePortfolioState();
    const { is3bProductGroupSelector } = useCreatePortfolioSelectors();
    const { getMandatoryStep } = useGetMandatoryStep(is3bProductGroupSelector);

    const { pathname } = useLocation();

    const singleArrayItems = useMemo(() => flattenArray(adaptedConfig), [adaptedConfig]);
    const totalNumberOfSteps = useMemo(() => singleArrayItems?.length, [singleArrayItems?.length]);
    const totalStepsCompleted = useMemo(() => singleArrayItems?.filter(f => f.id < selectedStep.id! && !selectedStep.id!.startsWith(f.id))?.length, [selectedStep, singleArrayItems]);
    const selectedStepIndex = useMemo(() => singleArrayItems?.findIndex(f => f.id === selectedStep.id), [selectedStep]);

    const getAdaptedConfig = useCallback((configItems: AdaptedConfigType[], parentId: string = '') => configItems?.map((item, index) => {
        const newId = parentId ? `${parentId}.${index + 1}` : `${index + 1}`;

        return {
            ...item,
            id: newId,
            children: item.children?.length ? getAdaptedConfig(item.children, newId) : null,
        };
    }), [config]);

    const toggleSection = useCallback((key) => {
        setShowSection(prev => ({
            ...prev,
            [key]: !isNil(prev[key]) ? !prev[key] : false,
        }));
    }, []);

    const configuredProductClicked = sessionStorage.getItem('configuredProductClicked');

    const handleCompletedSteps = useCallback((item) => {
        setCompletedSteps(prev => {
            if (prev.includes(+(item?.id as string))) {
                return prev;
            }

            const calculatedCompletedSteps = selectedStep?.id ? [...new Set([...prev, ...getNumbersBetween(+(prev[prev.length - 1] || 1), +(selectedStep.id as string), is3bProductGroupSelector ? 0.1 : 1)])] : [];

            let nextCompletedSteps = [...calculatedCompletedSteps];

            // debugger;

            if (isProductSetup && configuredProductClicked === 'false' && is3bProductGroupSelector && (+(selectedStep.id as string) === 2)) {
                nextCompletedSteps = calculatedCompletedSteps?.filter(s => ((s <= +(selectedStep.id as string)) && s !== 2));
            } else if (isProductSetup && configuredProductClicked === 'false' && !is3bProductGroupSelector && (+(selectedStep.id as string) === 1)) {
                nextCompletedSteps = calculatedCompletedSteps?.filter(s => ((s <= +(selectedStep.id as string))));
            } else if (isProductSetup && configuredProductClicked === 'true' && is3bProductGroupSelector && +(selectedStep.id as string) === 2.1) {
                nextCompletedSteps = calculatedCompletedSteps?.concat([2.1, 2.2, 2.3, 2.4, 2.5, 2.6]);
            } else if (isProductSetup && configuredProductClicked === 'true' && !is3bProductGroupSelector && +(selectedStep.id as string) === 2 && !calculatedCompletedSteps.includes(6)) {
                nextCompletedSteps = selectedStep?.position > 0 ? calculatedCompletedSteps?.concat([1, 2, 3, 4, 5, 6]) : [1];
            } else if (isProductSetup && configuredProductClicked === 'true' && !is3bProductGroupSelector && +(selectedStep.id as string) === 1 && calculatedCompletedSteps.includes(6)) {
                nextCompletedSteps = [1];
                sessionStorage.removeItem('configuredProductClicked');
            } else if (continueDisabledFromShoppingCart && continueDisabledFromShoppingCart === 'true') {
                nextCompletedSteps = is3bProductGroupSelector ? calculatedCompletedSteps?.filter(s => s !== 2) : calculatedCompletedSteps?.filter(s => s !== 1);
            } else if (continueDisabledFromShoppingCart && continueDisabledFromShoppingCart === 'false') {
                nextCompletedSteps = is3bProductGroupSelector ? calculatedCompletedSteps.concat([2]) : calculatedCompletedSteps.concat([1]);
            }

            return nextCompletedSteps;
        });
    }, [configuredProductClicked, isProductSetup, continueDisabledFromShoppingCart, selectedStep, is3bProductGroupSelector]);

    const getSelectedStep = useCallback((configItems?: AdaptedConfigType[]) => configItems?.forEach((item: StepperConfigType) => {
        handleCompletedSteps(item);

        if (item.path === pathname || item.position === currentStepNumber) {
            setSelectedStep(item);

            return;
        }
        if (item.children?.length) {
            getSelectedStep(item.children);
        }
    }), [pathname, currentStepNumber]);

    const handleJumpStep = useCallback((item: AdaptedConfigType) => {
        if (item?.id === '1') {
            return;
        }
        const mandatoryStepKey = getMandatoryStep({
            step: item, currentStep: selectedStep, steps: adaptedConfig as AdaptedConfigType[], completedSteps,
        });

        setClickedStep(mandatoryStepKey as string);

        if (item.label !== 'AdvisoryTypeSelection') {
            setStep({
                stepKey: mandatoryStepKey || item?.label,
            });
        }
        unlockNavigationAction();
    }, [adaptedConfig, selectedStep, getMandatoryStep, setStep, unlockNavigationAction, completedSteps]);

    const renderItems = useCallback((configItems?: AdaptedConfigType[]) => configItems?.sort((a, b) => a.position - b.position)
        .map((item, index) => {
            if (item.children?.length) {
                const isVisible = isNil(showSection[item.id!]) || !!showSection[item.id!];

                return (
                    <section data-id={item.id} key={item?.id || index}>
                        <div
                            role="button"
                            tabIndex={0}
                            className="collapsable"
                            onKeyDown={() => {
                            }}
                        >
                            <StepperItem
                                name={item.name}
                                isSelected={selectedStep?.id === item.id || (item.id! < selectedStep.id! && selectedStep.id!.startsWith(item.id!))}
                                // isCompleted={completedSteps.includes(+(item.id as string))}
                                isCompleted={item.id! < selectedStep.id!}
                                isMandatorySelected={!!clickedStep && item.label === clickedStep}
                                // handleJumpStep={() => handleJumpStep(item)}
                            />
                            <Icon
                                type={ICON_TYPE_DROPDOWN}
                                size={12}
                                className={isVisible ? 'reverse' : ''}
                                onClick={() => toggleSection(item.id)}
                            />
                        </div>
                        {isVisible && <div className="children">{renderItems(item.children)}</div>}
                    </section>
                );
            }

            return (
                <StepperItem
                    key={item?.id || index}
                    data-id={item.id}
                    name={item.name}
                    isSelected={selectedStep?.id === item.id}
                    // isCompleted={completedSteps.includes(+(item.id as string))}
                    isCompleted={item.id! < selectedStep.id!}
                    isMandatorySelected={!!clickedStep && item.label === clickedStep}
                    // handleJumpStep={() => handleJumpStep(item)}
                />
            );
        }), [selectedStep, showSection, completedSteps, adaptedConfig, handleJumpStep, toggleSection, clickedStep]);

    useEffect(() => {
        const percentage = ((totalStepsCompleted * 100) / totalNumberOfSteps);

        setCompletionPercentage(Math.trunc(percentage) || 0);
    }, [totalNumberOfSteps, totalStepsCompleted]);

    useEffect(() => {
        getSelectedStep(adaptedConfig);
    }, [getSelectedStep, adaptedConfig]);

    useEffect(() => {
        setAdaptedConfig(getAdaptedConfig(config));
    }, [getAdaptedConfig, config]);

    useEffect(() => () => clear(), []);

    if (!config?.length) {
        return (
            <div className="stepper-loader-wrapper">
                <Loader/>
            </div>
        );
    }

    return (
        <section className={`stepper ${className || ''}`}>
            {title && (
                <h1>
                    {t(title)}
                    {' '}
                    <div>{productType}</div>
                </h1>
            )}
            <div
                className="percentage"
                style={{ background: `radial-gradient(closest-side, white 89%, transparent 90% 100%), conic-gradient(#2167ae ${completionPercentage}%, white 0)` }}
            >
                {`${completionPercentage}%`}
            </div>
            <p className="steps-title">
                {t('stepper.stepOfSteps', {
                    currentStep: selectedStepIndex + 1,
                    total: totalNumberOfSteps,
                })}
            </p>
            {renderItems(adaptedConfig)}
        </section>
    );
};

export default Stepper;
